import { RoasterList } from "@bcc/components/admin";
import { AdminNav } from "@bcc/components/admin/admin-nav";
import {
  Avatar,
  AvatarFallback,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@bcc/components/ui";
import { useAdminImpersonation } from "@bcc/hooks/use-admin-impersonation";
import { ROUTE_PATHS } from "@bcc/lib/constants";
import { useSFDCAuthState } from "@client-shared/providers/auth";
import { getVendors } from "@client-shared/services/api";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { mapVendorToRoasterListItem, parseUserDetailsFromIdToken, TEST_IDS } from "./admin-roasters-page.utils";

const ADMIN_VENDOR_LIST_QUERY_KEY = ["admin-vendor-list"] as const;

/**
 * Admin Roaster List Page
 *
 * Allows admins to view and impersonate roasters.
 */
export function AdminRoastersPage() {
  const { t } = useTranslation();
  const [authState] = useSFDCAuthState();
  const navigate = useNavigate();
  const { handleImpersonate, isSelectingVendor, selectionError } = useAdminImpersonation();

  const {
    data: vendors = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ADMIN_VENDOR_LIST_QUERY_KEY,
    queryFn: getVendors,
  });

  const roasters = React.useMemo(() => {
    return vendors.filter((vendor) => vendor.isBccEnabled).map(mapVendorToRoasterListItem);
  }, [vendors]);
  const parsedUserDetails = React.useMemo(() => parseUserDetailsFromIdToken(authState.sfdcIdToken), [authState.sfdcIdToken]);

  const userName = parsedUserDetails.name ?? t("admin.roasterList.userSection.defaultName");
  const userInitials = React.useMemo(() => {
    const names = userName.split(" ").filter(Boolean);
    if (names.length === 0) {
      return "RA";
    }

    const firstInitial = names[0]?.charAt(0).toUpperCase() ?? "";
    const secondInitial = names[1]?.charAt(0).toUpperCase() ?? "";
    return `${firstInitial}${secondInitial}`;
  }, [userName]);

  const errorMessage = error instanceof Error ? error.message : "Unknown error";
  const handleLogout = React.useCallback(() => {
    navigate(ROUTE_PATHS.LOGOUT);
  }, [navigate]);

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6">
      <AdminNav />
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{t("admin.roasterList.pageTitle")}</h1>
          <p className="mt-2 text-muted-foreground">{t("admin.roasterList.pageDescription")}</p>
        </div>

        <DropdownMenu>
          <DropdownMenuTrigger asChild testId={TEST_IDS.userMenuTrigger}>
            <Button className="relative h-9 w-9 rounded-full cursor-pointer outline-none" aria-label={t("userNav.userMenu")}>
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-primary text-primary-foreground">{userInitials}</AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent testId={TEST_IDS.userMenuContent} className="w-[18rem]" align="end" forceMount>
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none" data-testid={TEST_IDS.userName}>
                  {userName}
                </p>
                {parsedUserDetails.email ? (
                  <p className="text-xs leading-none text-muted-foreground" data-testid={TEST_IDS.userEmail}>
                    {parsedUserDetails.email}
                  </p>
                ) : null}
                {authState.role ? (
                  <p className="text-xs leading-none text-muted-foreground" data-testid={TEST_IDS.userRole}>
                    {authState.role}
                  </p>
                ) : null}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem testId={TEST_IDS.logoutButton} className="text-destructive" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>{t("userNav.logOut")}</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {isError ? (
        <p className="text-sm text-destructive" data-testid={TEST_IDS.vendorsLoadError}>
          {t("admin.roasterList.vendorLoadError", { errorMessage })}
        </p>
      ) : null}

      {selectionError ? (
        <p className="text-sm text-destructive" data-testid={TEST_IDS.vendorSelectError}>
          {t("admin.roasterList.vendorSelectError", {
            errorMessage: selectionError,
          })}
        </p>
      ) : null}

      <RoasterList roasters={roasters} onImpersonate={handleImpersonate} isLoading={isLoading || isSelectingVendor} />
    </div>
  );
}
